<script setup lang="ts">
import { Notification as NotificationSchema, todayInParis } from '@cockpit/shared';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppHeader from '../components/AppHeader.vue';
import AssistantDrawer from '../components/AssistantDrawer.vue';
import AssistantFab from '../components/AssistantFab.vue';
import FeedbackFab from '../components/FeedbackFab.vue';
import FeedbackPanel from '../components/FeedbackPanel.vue';
import OfflineBanner from '../components/OfflineBanner.vue';
import OfflineConflictSheet from '../components/OfflineConflictSheet.vue';
import PlanModal from '../components/PlanModal.vue';
import QuoteDraftDrawer from '../components/quote/QuoteDraftDrawer.vue';
import UpdateNotice from '../components/nouveautes/UpdateNotice.vue';
import WhatsNew from '../components/nouveautes/WhatsNew.vue';
import SignatureParty from '../components/SignatureParty.vue';
import { api } from '../api';
import { LOGIN_PATH } from '../navigation';
import { NOUVEAUTES_PATH } from '../nouveautes';
import { isOfflineScreen } from '../offline/cache';
import { forgetDevice } from '../pwa/push';
import { useAssistantStore } from '../stores/assistant';
import { useAuthStore } from '../stores/auth';
import { useFeedbackPanelStore } from '../stores/feedbackPanel';
import { useNouveautesStore } from '../stores/nouveautes';
import { useOfflineStore } from '../stores/offline';
import { useStreamStore } from '../stores/stream';
import { useToastStore } from '../stores/toast';

const auth = useAuthStore();
const assistant = useAssistantStore();
const feedbackPanel = useFeedbackPanelStore();
const stream = useStreamStore();
const toast = useToastStore();
const router = useRouter();
/** Lot BR : la page Projets prend toute la largeur de l'écran. */
const currentRoute = useRoute();
/** Lot BA : encart « Nouvelle version », « Quoi de neuf », point vert des onglets. */
const nouveautes = useNouveautesStore();
/** Lot AW : hors connexion (bandeau, écrans non gardés, conflits à trancher). */
const offline = useOfflineStore();
const conflictsOpen = ref(false);
watch(
  () => offline.conflicts.length,
  (count, before) => {
    if (count > (before ?? 0)) conflictsOpen.value = true;
    if (count === 0) conflictsOpen.value = false;
  },
  { immediate: true },
);
const today = computed(() => auth.config?.today ?? todayInParis());

let unsubscribe: (() => void) | null = null;
onMounted(() => {
  // Notifications de l'appli ouverte (la fête de signature a son propre écran).
  unsubscribe = stream.on('notification', NotificationSchema, (notification) => {
    if (notification.kind !== 'quote_signed')
      toast.show(`${notification.title} · ${notification.body}`, 5000);
  });
  stream.start();
  nouveautes.start();
});
onBeforeUnmount(() => {
  unsubscribe?.();
  nouveautes.stop();
  stream.stop();
});

/**
 * Pied de page : version déployée (« Cockpit 0.1.0 · 828caea · 13/10 09:40 »), à comparer à `git log --oneline`.
 * Lot BA : le numéro de version mène à la page Nouveautés.
 */
const buildVersion = computed(() => auth.config?.build?.version ?? '');
const buildDetails = computed(() => {
  const build = auth.config?.build;
  if (!build) return '';
  const when = build.committedAt
    ? new Date(build.committedAt).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Paris',
      })
    : null;
  return [build.commit, when].filter(Boolean).join(' · ');
});

async function logout(): Promise<void> {
  // Lot AW : les changements pas encore envoyés seraient perdus (tout est effacé à la déconnexion).
  const waiting = offline.ops.length;
  if (
    waiting > 0 &&
    !window.confirm(
      `${waiting} changement${waiting > 1 ? 's' : ''} pas encore envoyé${waiting > 1 ? 's' : ''} ser${waiting > 1 ? 'ont' : 'a'} perdu${waiting > 1 ? 's' : ''}. Se déconnecter quand même ?`,
    )
  )
    return;
  stream.stop();
  // Décision du PM : la déconnexion retire l'abonnement push de cet appareil.
  await forgetDevice((endpoint) => api.pushUnsubscribe(endpoint));
  await auth.logout();
  await router.push(LOGIN_PATH);
}
</script>

<template>
  <template v-if="auth.me">
    <AppHeader :me="auth.me" :today="today" @logout="logout" />
    <OfflineBanner @conflicts="conflictsOpen = true" />
    <main
      class="wrap"
      :class="{
        'wrap-full': currentRoute.name === 'projets' || currentRoute.path === '/support',
        'main-fit': currentRoute.path === '/support',
      }"
      :data-stream="stream.connected ? 'on' : 'off'"
    >
      <RouterView v-slot="{ Component, route }">
        <!-- Lot AW : sans réseau, seuls À faire, Projets et Agenda sont gardés sur le téléphone. -->
        <div
          v-if="!offline.online && !isOfflineScreen(route.name)"
          class="card offline-unavailable"
          data-testid="offline-unavailable"
        >
          <h2>Pas disponible hors connexion</h2>
          <p class="muted">
            Les chiffres, la trésorerie et les dépenses ne sont pas gardés sur le téléphone. À faire, Projets
            et l’Agenda restent consultables ; cet écran revient avec le réseau.
          </p>
          <RouterLink class="btn sm primary" to="/a-faire">Aller à À faire</RouterLink>
        </div>
        <component :is="Component" v-else />
      </RouterView>
    </main>
    <footer v-if="buildVersion" class="wrap app-foot" data-testid="app-version">
      <span>Cockpit </span>
      <RouterLink
        class="nv-version-link"
        :to="NOUVEAUTES_PATH"
        :title="`Nouveautés de la version ${buildVersion}`"
        data-testid="app-version-link"
        >{{ buildVersion }}</RouterLink
      ><template v-if="buildDetails"> · {{ buildDetails }}</template>
    </footer>
    <!-- Lot AT : brouillon de devis (grand volet), sous l'écran de validation qu'il ouvre. -->
    <QuoteDraftDrawer />
    <PlanModal />
    <SignatureParty />
    <!-- Lot E2 : l'assistant, accessible depuis tous les écrans. Lot AQ : la bulle « Faire un retour » au-dessus ;
         un seul panneau ouvert à la fois, les bulles se masquent tant qu'il l'est. -->
    <div v-if="!assistant.open && !feedbackPanel.open" class="fabs">
      <FeedbackFab />
      <AssistantFab />
    </div>
    <AssistantDrawer />
    <FeedbackPanel />
    <OfflineConflictSheet :open="conflictsOpen" @close="conflictsOpen = false" />
    <UpdateNotice />
    <WhatsNew />
  </template>
</template>
